import { useState, ReactNode } from 'react'
import {
  Bot,
  User,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Copy,
  ArrowRight,
  Lightbulb,
  Wrench,
  Terminal,
  FileCode,
  Info,
  Link as LinkIcon,
  StepForward,
} from 'lucide-react'
import { format } from 'date-fns'
import {
  ChatMessage as ChatMessageType,
  AssistantChatMessage,
  AssistantChatMessageContent,
} from '@/types'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import useAuthStore from '@/stores/useAuthStore'
import { safeParseDate } from '@/lib/date-utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

interface ChatMessageProps {
  message: ChatMessageType
  onSuggestionClick?: (suggestion: string) => void
}

const CopyToClipboardButton = ({ text }: { text: string }) => {
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      toast({
        description: 'Copied to clipboard!',
      })
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Button
      onClick={handleCopy}
      size="icon"
      variant="ghost"
      className="h-7 w-7"
    >
      {copied ? (
        <CheckCircle className="h-4 w-4 text-success" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
      <span className="sr-only">Copy</span>
    </Button>
  )
}

const StructuredContentSection = ({
  title,
  icon: Icon,
  children,
  className,
}: {
  title: string
  icon: React.ElementType
  children: ReactNode
  className?: string
}) => {
  if (!children) return null
  return (
    <div className={cn('space-y-2', className)}>
      <h4 className="font-semibold text-sm flex items-center gap-2 text-foreground/80">
        <Icon className="h-4 w-4" />
        {title}
      </h4>
      <div className="pl-6 text-sm prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-pre:my-1">
        {children}
      </div>
    </div>
  )
}

const AssistantMessageContent = ({
  content,
  onSuggestionClick,
}: {
  content: string | AssistantChatMessageContent
  onSuggestionClick?: (suggestion: string) => void
}) => {
  if (typeof content === 'string') {
    return <p className="whitespace-pre-wrap">{content}</p>
  }

  if (typeof content !== 'object' || content === null) return null

  const hasDiagnosticInfo =
    content.what_is_broken || content.what_broke_it || content.patch_to_fix_it

  return (
    <div className="space-y-4">
      <StructuredContentSection title="Thoughts" icon={Lightbulb}>
        {content.thoughts && <p>{content.thoughts}</p>}
      </StructuredContentSection>

      {hasDiagnosticInfo && (
        <Accordion type="single" collapsible className="w-full -mx-2">
          <AccordionItem value="diagnostics" className="border-b-0">
            <AccordionTrigger className="px-2 hover:no-underline">
              <span className="flex items-center gap-2 font-semibold text-sm text-foreground/80">
                <Wrench className="h-4 w-4" /> Diagnostic Analysis
              </span>
            </AccordionTrigger>
            <AccordionContent className="pt-2 space-y-4 pl-2">
              <StructuredContentSection title="What is broken?" icon={Wrench}>
                {content.what_is_broken && <p>{content.what_is_broken}</p>}
              </StructuredContentSection>
              <StructuredContentSection title="What broke it?" icon={Wrench}>
                {content.what_broke_it && <p>{content.what_broke_it}</p>}
              </StructuredContentSection>
              <StructuredContentSection title="Patch to fix it" icon={FileCode}>
                {content.patch_to_fix_it && (
                  <div className="bg-code-block p-2 rounded-md flex items-start justify-between gap-2">
                    <pre className="text-sm font-mono text-foreground overflow-x-auto">
                      <code>
                        {content.patch_to_fix_it.replace(/```.*\n|```/g, '')}
                      </code>
                    </pre>
                    <CopyToClipboardButton
                      text={content.patch_to_fix_it.replace(/```.*\n|```/g, '')}
                    />
                  </div>
                )}
              </StructuredContentSection>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      <StructuredContentSection title="Commands" icon={Terminal}>
        {content.commands && content.commands.length > 0 && (
          <div className="space-y-2">
            {content.commands.map((cmd, i) => (
              <div
                key={i}
                className="bg-code-block p-2 rounded-md flex items-center justify-between gap-2"
              >
                <pre className="text-sm font-mono text-foreground overflow-x-auto">
                  <code>{cmd}</code>
                </pre>
                <CopyToClipboardButton text={cmd} />
              </div>
            ))}
          </div>
        )}
      </StructuredContentSection>

      <StructuredContentSection title="Collected Information" icon={Info}>
        {content.collected_resources_informations &&
          content.collected_resources_informations.length > 0 && (
            <ul>
              {content.collected_resources_informations.map((info, i) => (
                <li key={i}>{info}</li>
              ))}
            </ul>
          )}
      </StructuredContentSection>

      <StructuredContentSection title="References" icon={LinkIcon}>
        {content.references && content.references.length > 0 && (
          <ul>
            {content.references.map((ref, i) => (
              <li key={i}>
                <a
                  href={ref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center gap-1 break-all"
                >
                  {ref} <ExternalLink className="h-3 w-3 flex-shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </StructuredContentSection>

      <StructuredContentSection title="Issues" icon={AlertTriangle}>
        {content.issues && content.issues.length > 0 && (
          <ul className="space-y-1">
            {content.issues.map((issue, i) => (
              <li
                key={i}
                className="flex items-start gap-2 bg-warning/10 p-2 rounded-md text-warning"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{issue}</span>
              </li>
            ))}
          </ul>
        )}
      </StructuredContentSection>

      {content.next_steps && content.next_steps.length > 0 && (
        <div className="space-y-2 pt-4 mt-4 border-t">
          <h4 className="font-semibold text-sm flex items-center gap-2 text-foreground/80">
            <StepForward className="h-4 w-4" /> Suggested next steps:
          </h4>
          <div className="grid grid-cols-1 gap-2">
            {content.next_steps.map((step, i) => (
              <div
                key={i}
                onClick={() => onSuggestionClick?.(step)}
                onKeyDown={(e) => {
                  if (
                    (e.key === 'Enter' || e.key === ' ') &&
                    onSuggestionClick
                  ) {
                    e.preventDefault()
                    onSuggestionClick(step)
                  }
                }}
                role="button"
                tabIndex={0}
                className="bg-background p-3 rounded-md flex items-center justify-between gap-2 cursor-pointer hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <p className="text-sm font-medium">{step}</p>
                <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export const ChatMessage = ({
  message,
  onSuggestionClick,
}: ChatMessageProps) => {
  const isUser = message.role === 'user'
  const user = useAuthStore((state) => state.user)
  const messageDate = safeParseDate(message.timestamp)

  return (
    <div
      className={cn(
        'flex items-start gap-4 animate-message-in',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      {!isUser && (
        <Avatar className="h-8 w-8">
          <AvatarFallback>
            <Bot className="h-5 w-5" />
          </AvatarFallback>
        </Avatar>
      )}
      <div
        className={cn(
          'flex flex-col max-w-[80%]',
          isUser ? 'items-end' : 'items-start',
        )}
      >
        <div
          className={cn(
            'p-3 rounded-lg shadow-sm',
            isUser
              ? 'bg-primary text-primary-foreground rounded-br-none'
              : 'bg-card text-card-foreground rounded-bl-none',
          )}
        >
          {typeof message.content === 'string' ? (
            <p className="whitespace-pre-wrap">{message.content}</p>
          ) : (
            <AssistantMessageContent
              content={message.content as AssistantChatMessageContent}
              onSuggestionClick={onSuggestionClick}
            />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {messageDate ? format(messageDate, 'p') : null}
        </p>
      </div>
      {isUser && (
        <Avatar className="h-8 w-8">
          <AvatarImage src={user?.picture} />
          <AvatarFallback>
            <User className="h-5 w-5" />
          </AvatarFallback>
        </Avatar>
      )}
    </div>
  )
}
